import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Button } from '@Components/index';
import Modal from '@Components/Modal';
import TimePickerCustom from '@Components/TimePickerCustom';
import {
  getDateFromString,
  getStringFromDate,
} from '@Containers/Settings/Settings.utils';
import { useEditTimeFrameByIdMutation } from '@Services/settings.service';
import { BaseAlertProps } from '@Typings/global.types';
import { getCallTimeFrameFromFeatureValue } from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  CallTimeFrameEditModalInputs,
  CallTimeFrameEditModalProps,
  CallTimeFrameEditModalRequest,
} from './CallTimeFrameEditModal.types';

const CallTimeFrameEditModal: FunctionComponent<CallTimeFrameEditModalProps> = (
  props: CallTimeFrameEditModalProps
): JSX.Element => {
  const { callTimeFrameEditData, isOpen, setIsOpen } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  // edit time frame values
  const [editTimeFrame, editTimeFrameMethods] = useEditTimeFrameByIdMutation();

  const required = t('common.formErrorMessages.required');
  const typeErrorText = 'Invalid time format. Must be hh:mm (A|P)M';
  const methods = useForm<CallTimeFrameEditModalInputs>({
    resolver: yupResolver(
      yup.object({
        callAllowedStartTime: yup
          .date()
          .nullable()
          .typeError(typeErrorText)
          .required(required),
        callAllowedEndTime: yup
          .date()
          .nullable()
          .typeError(typeErrorText)
          .required(required),
      })
    ),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /**
   * Edit time frame values form methods: updated values on submit
   */
  const onSubmitHandler: SubmitHandler<CallTimeFrameEditModalRequest> = (
    formData
  ) => {
    if (formData) {
      return editTimeFrame({
        id: callTimeFrameEditData.id,
        callAllowedStartTime: getStringFromDate(formData.callAllowedStartTime),
        callAllowedEndTime: getStringFromDate(formData.callAllowedEndTime),
      });
    }
  };

  /**
   * Error and Success alerts for editing time frame values
   */
  useEffect(() => {
    if (editTimeFrameMethods.error) {
      triggerAlert(editTimeFrameMethods.error, setAlertState);
    }
    if (editTimeFrameMethods.isSuccess) {
      setIsOpen(false);
    }
  }, [
    editTimeFrameMethods.isSuccess,
    editTimeFrameMethods.error,
    editTimeFrameMethods.data,
    triggerAlert,
    editTimeFrameMethods,
    setIsOpen,
  ]);

  const closeModal = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const timeFrame = getCallTimeFrameFromFeatureValue(
      callTimeFrameEditData.value
    );
    methods.reset({
      callAllowedStartTime: getDateFromString(timeFrame.startTimeParsed),
      callAllowedEndTime: getDateFromString(timeFrame.endTimeParsed),
    });
  }, [methods, callTimeFrameEditData]);

  return (
    <>
      <Modal
        open={isOpen}
        headerText={`Edit ${callTimeFrameEditData.name}`}
        onClose={() => closeModal()}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                sx={{ padding: '3rem 5.5rem 2.4rem' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  my={2}
                  gap={0.8}
                >
                  <TimePickerCustom
                    name="callAllowedStartTime"
                    label={`${t('adminSettings.Start Time')} *`}
                  />
                  <Box
                    sx={{
                      borderTop: '0.2rem solid #767676',
                      minWidth: '1.4rem',
                    }}
                  />
                  <TimePickerCustom
                    name="callAllowedEndTime"
                    label={`${t('adminSettings.End Time')} *`}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  borderTop: '0.1rem solid #A3ABB2',
                  padding: '3rem 2.4rem 2.4rem',
                }}
              >
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    onClick={() => closeModal()}
                    color="secondary"
                    variant="outlined"
                    sx={{ ml: 'auto', mr: 1.6 }}
                  >
                    {t('common.messages.Cancel')}
                  </Button>
                  <Button type="submit" color="primary">
                    {t('common.messages.Submit')}
                  </Button>
                </Box>
              </Box>
            </>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default CallTimeFrameEditModal;
